'use client';
/** @jsxImportSource @emotion/react */

import React, { type ElementType, forwardRef, type Ref } from 'react';

import {
  type OverridableComponent,
  type OverrideProps,
} from '@mui/material/OverridableComponent';
import { useTheme } from '@mui/material/styles';
import MuiTypography, {
  type TypographyProps as MuiTypographyProps,
  type TypographyTypeMap,
} from '@mui/material/Typography';

type ExcludedMuiProps = 'gutterBottom' | 'variantMapping';
type Decoration = 'none' | 'strike-through' | 'underline';
type CustomProps = {
  color?: MuiTypographyProps['color'] | 'forceWhite';
  component?: ElementType;
  /**
   * The decoration of the text content.
   * @default 'none'
   */
  decoration?: Decoration;
};

const decorationStyles: Record<Decoration, string> = {
  none: 'none',
  'strike-through': 'line-through',
  underline: 'underline',
};

type ZeusTypographyTypeMap<
  P = CustomProps,
  D extends ElementType = TypographyTypeMap['defaultComponent'],
> = {
  defaultComponent: D;
  props: Omit<TypographyTypeMap['props'], ExcludedMuiProps> & P;
};

/**
 * @deprecated Please use `Typography` from `@zeus/core/next` instead.
 * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-typography-migration--typography-migration-guide Migration guide}
 */
export type TypographyProps<
  D extends ElementType = TypographyTypeMap['defaultComponent'],
  P = CustomProps,
> = OverrideProps<TypographyTypeMap<P, D>, D>;

const TypographyComponent = (
  { color, decoration = 'none', sx, ...props }: TypographyProps,
  ref: Ref<HTMLSpanElement>
) => {
  const theme = useTheme();
  const resolvedColor =
    color === 'forceWhite' ? theme.palette.common.white : color;

  return (
    <MuiTypography
      {...props}
      ref={ref}
      sx={[
        ...(Array.isArray(sx) ? sx : [sx]),
        { textDecoration: decorationStyles[decoration] },
      ]}
      color={resolvedColor}
    />
  );
};

TypographyComponent.displayName = 'Typography';

/**
 * @deprecated Please use `Typography` from `@zeus/core/next` instead.
 * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-typography-migration--typography-migration-guide Migration guide}
 */
export const Typography = forwardRef(
  TypographyComponent
) as OverridableComponent<ZeusTypographyTypeMap>;

export type ExcludedMuiTypographyProps = ExcludedMuiProps;
